import { localToday } from '@/lib/dateRange';
// iter453 — Pure builders for the dashboard "Overview" & "Money Movement"
// stat-card arrays. Extracted from DashboardPage.jsx so the page body
// stays focused on layout. Both functions are pure — no React, no
// hooks — they simply project the already-fetched stats into the shape
// the StatCardsGrid / payment-mode tiles expect.
//
// Behaviour is byte-for-byte identical to the previous inline version.
import {
  DollarSign, TrendingDown, TrendingUp, Building2, AlertCircle,
  Wallet, CreditCard,
} from 'lucide-react';

export function buildStatCards({ stats, vis, tr, dailyTrend, dateFilter, inclVat = false }) {
  const dateQs = dateFilter.start
    ? `?start_date=${dateFilter.start}&end_date=${dateFilter.end || localToday()}`
    : '';

  return [
    {
      title: `${tr('total_sales')} (${inclVat ? 'Incl. VAT' : 'Excl. VAT'})`,
      numValue: (inclVat ? stats?.gross_sales : stats?.total_sales) ?? 0,
      prev: stats?.prev_sales,
      icon: DollarSign,
      color: 'text-success',
      bgColor: 'bg-success/10',
      testId: 'total-sales-card',
      sparkline: dailyTrend.sales,
      sparkColor: '#22C55E',
      link: `/sales${dateQs}`,
      subItems: [
        { label: inclVat ? 'Net excl. VAT' : 'Gross incl. VAT', value: inclVat ? stats?.total_sales : stats?.gross_sales, color: 'text-muted-foreground' },
        { label: 'VAT collected', value: stats?.sales_vat, color: 'text-muted-foreground' },
        { label: 'Cash', value: stats?.cash_sales, color: 'text-success' },
        { label: 'Bank', value: stats?.bank_sales, color: 'text-info' },
        ...(stats?.online_sales > 0 ? [{ label: 'Online (Total)', value: stats?.online_sales, color: 'text-purple-600' }] : []),
        ...(stats?.platform_received > 0 ? [{ label: 'Online Received', value: stats?.platform_received, color: 'text-success', prefix: '  ' }] : []),
        ...(stats?.online_receivable > 0 ? [{ label: 'Online Pending', value: stats?.online_receivable, color: 'text-warning', prefix: '  ' }] : []),
      ],
      extraInfo: stats?.online_sales > 0 ? {
        received: stats?.received_sales ?? 0,
        receivable: stats?.online_receivable ?? 0,
      } : null,
    },
    ...(!vis.hide_financials ? [{
      title: tr('total_expenses'),
      numValue: (stats?.total_expenses ?? 0) + (inclVat ? (stats?.expenses_vat || 0) : 0),
      prev: stats?.prev_expenses,
      pct: stats?.expenses_pct_of_sales,
      invert: true,
      icon: TrendingDown,
      color: 'text-error',
      bgColor: 'bg-error/10',
      testId: 'total-expenses-card',
      sparkline: dailyTrend.expenses,
      sparkColor: '#EF4444',
      link: `/expenses${dateQs}`,
      subItems: [
        { label: 'Cash Paid', value: stats?.expenses_cash || 0, color: 'text-success' },
        { label: 'Bank Paid', value: stats?.expenses_bank || 0, color: 'text-info' },
        ...(stats?.expense_by_category
          ? Object.entries(stats.expense_by_category)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 2)
              .map(([cat, amt]) => ({ label: cat, value: amt, color: 'text-error' }))
          : []),
      ],
    }] : []),
    ...(!vis.hide_financials ? [{
      title: tr('supplier_payments'),
      numValue: stats?.total_supplier_payments ?? 0,
      pct: stats?.sp_pct_of_sales,
      icon: Building2,
      color: 'text-info',
      bgColor: 'bg-info/10',
      testId: 'supplier-payments-card',
      subItems: [
        { label: 'Cash Paid', value: stats?.sp_cash || 0, color: 'text-success' },
        { label: 'Bank Paid', value: stats?.sp_bank || 0, color: 'text-info' },
      ],
    }] : []),
    ...(!vis.hide_profit ? [{
      title: tr('net_profit'),
      numValue: stats?.net_profit ?? 0,
      prev: stats?.prev_net,
      pct: stats?.profit_pct_of_sales,
      icon: TrendingUp,
      color: stats?.net_profit >= 0 ? 'text-success' : 'text-error',
      bgColor: stats?.net_profit >= 0 ? 'bg-success/10' : 'bg-error/10',
      testId: 'net-profit-card',
      sparkline: dailyTrend.profit,
      sparkColor: '#F5841F',
    }] : []),
    {
      title: tr('pending_credits'),
      numValue: stats?.pending_credits ?? 0,
      icon: AlertCircle,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      testId: 'pending-credits-card',
    },
  ];
}

export function buildPaymentModeCards({ stats }) {
  return [
    {
      title: 'Cash in Hand',
      numValue: stats?.cash_in_hand ?? 0,
      icon: Wallet,
      color: 'text-cash',
      bgColor: 'bg-cash/10',
      testId: 'cash-sales-card',
      subItems: [
        { label: 'Cash Sales', value: stats?.cash_sales, color: 'text-success' },
        { label: 'Cash Expenses', value: -(stats?.expenses_cash || 0), color: 'text-error' },
        { label: 'Cash Supplier', value: -(stats?.sp_cash || 0), color: 'text-error' },
      ],
    },
    {
      title: 'Bank Balance',
      numValue: stats?.bank_in_hand ?? 0,
      icon: Building2,
      color: 'text-bank',
      bgColor: 'bg-bank/10',
      testId: 'bank-sales-card',
      subItems: [
        { label: 'Bank Sales', value: stats?.bank_sales, color: 'text-info' },
        { label: 'Bank Expenses', value: -(stats?.expenses_bank || 0), color: 'text-error' },
        { label: 'Platform Received', value: stats?.platform_received || 0, color: 'text-success' },
      ],
    },
    {
      title: 'Online Receivable',
      numValue: stats?.online_receivable ?? 0,
      icon: CreditCard,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      testId: 'credit-sales-card',
      subItems: [
        { label: 'Total Online Sales', value: stats?.online_sales, color: 'text-purple-600' },
        { label: 'Platform Received', value: -(stats?.platform_received || 0), color: 'text-success' },
      ],
    },
  ];
}
